import type { CatalogWidgetSpec } from "../../redux/widgets.types"

/**
 * The picker's category filter.
 *
 * `other` exists so the tabs can never hide a widget: a data source this map
 * has not met yet still lands somewhere a user can reach, which is the whole
 * point of a catalog. Nothing is dropped for want of a category.
 */
export const WIDGET_CATEGORIES = [
  "all",
  "projects",
  "finance",
  "leads",
  "activity",
  "other"
] as const

export type WidgetCategory = (typeof WIDGET_CATEGORIES)[number]

/** The backend's own `data_source`, mapped onto the tabs people see. */
const BY_DATA_SOURCE: Record<string, WidgetCategory> = {
  projects: "projects",
  project: "projects",
  map: "projects",
  milestones: "projects",
  financial: "finance",
  finance: "finance",
  invoices: "finance",
  leads: "leads",
  crm: "leads",
  feed: "activity",
  activity_feed: "activity",
  alerts: "activity",
  notifications: "activity"
}

/** A feed or an alert is activity whatever it reads from. */
const BY_TYPE: Record<string, WidgetCategory> = {
  feed: "activity",
  alert: "activity"
}

const isCategory = (value: string): value is WidgetCategory =>
  (WIDGET_CATEGORIES as readonly string[]).includes(value)

/**
 * The backend's own `category` wins when it sends one — it knows about
 * widgets this app has not shipped UI for yet. Otherwise the data source
 * decides, then the widget type, and `other` catches the rest.
 */
export const categoryOf = (spec: CatalogWidgetSpec): WidgetCategory => {
  const declared = spec.category?.toLowerCase()
  if (declared && isCategory(declared) && declared !== "all") return declared
  return (
    BY_DATA_SOURCE[spec.data_source?.toLowerCase() ?? ""] ??
    BY_TYPE[spec.type?.toLowerCase() ?? ""] ??
    "other"
  )
}

/**
 * The one-line description under a catalog card.
 *
 * The registry's own `description` is preferred; the fallback describes what
 * the widget IS (its measure and shape) rather than inventing a claim about
 * trends or calculations it does not perform.
 */
/**
 * What each widget is FOR, keyed by the registry's own metric.
 *
 * The registry's `description` wins when the backend sends one. This table
 * covers the widgets this app ships UI for, because "A single headline
 * figure" told a reader nothing about which figure or why they would want it.
 * Each line states what the widget reports — never a trend, comparison or
 * calculation the widget does not actually perform.
 */
const BY_METRIC: Record<string, string> = {
  revenue: "Money invoiced in the selected period.",
  revenue_in_window: "Money invoiced inside the active date filter.",
  outstanding_receivables: "Invoiced money not yet collected.",
  gross_margin: "Income left after cost, as a percentage.",
  win_rate: "Share of leads that became projects.",
  active_projects: "Projects currently open.",
  delayed_projects: "Projects past a milestone's planned end.",
  pending_approvals: "Proposals and contracts waiting on a decision.",
  budget_health: "How much of the planned budget is committed.",
  cash_flow_series: "Money in against money out, period by period.",
  project_status_breakdown: "Projects split into on track, at risk and delayed.",
  portfolio_financial_snapshot:
    "Income, cost and profit — planned, to date and remaining.",
  next_best_actions: "The things most worth doing next, most urgent first.",
  upcoming_milestones: "Milestones with their dates and owners.",
  lead_engagement: "Leads added against leads engaged, over time.",
  marketing_source_performance: "Each lead source against its budget."
}

export const describeSpec = (
  spec: CatalogWidgetSpec,
  t: (key: string, fallback: string) => string
): string => {
  const own = spec.description?.trim()
  if (own) return own
  const byMetric = spec.metric ? BY_METRIC[spec.metric] : undefined
  if (byMetric) return t(`portfolio.catalog.describe.metric.${spec.metric}`, byMetric)
  // A widget this app has no copy for still says what SHAPE it is, which is
  // honest, rather than claiming a purpose nobody wrote down.
  const byType: Record<string, string> = {
    kpi: t("portfolio.catalog.describe.kpi", "A single headline figure."),
    chart: t("portfolio.catalog.describe.chart", "Shown as a chart."),
    table: t("portfolio.catalog.describe.table", "Shown as a list of rows."),
    list: t("portfolio.catalog.describe.list", "Shown as a list of rows."),
    snapshot: t("portfolio.catalog.describe.snapshot", "Shown as a table of figures."),
    feed: t("portfolio.catalog.describe.feed", "Recent items, newest first."),
    alert: t("portfolio.catalog.describe.alert", "A list of things to look at."),
    map: t("portfolio.catalog.describe.map", "Records placed on a map.")
  }
  return (
    byType[spec.type] ??
    t("portfolio.catalog.describe.generic", "Adds this widget to your dashboard.")
  )
}
